import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Map as MapIcon,
  MyLocation as LocationIcon,
  NotificationsActive as NudgeIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { MapContainer, Marker, Popup, TileLayer, Tooltip as MapTooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from 'react-i18next';
import { courierModuleService, CourierMap, CourierMapCourier } from './courierModuleService';
import { agoText, freshnessColor, freshnessHex, kmText, locationInfo, parseUtc } from './courierLocation';
import { MAP_MAX_ZOOM, MAP_TILE_ATTRIBUTION, MAP_TILE_URL, googleMapsPointUrl } from '../../../utils/mapTiles';

interface Props {
  open: boolean;
  onClose: () => void;
  branchId?: string | null;
  /** Açılışta gösterilecek kurye; null ise tüm kuryeler. */
  courierId?: number | null;
}

/** Harita açıkken konumlar bu aralıkla yenilenir. */
const REFRESH_MS = 15000;
/** Haritada merkez yokken (hiç konum yok) Türkiye görünür. */
const TURKEY_CENTER: [number, number] = [39.0, 35.0];

const escapeHtml = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));

const pin = (emoji: string, color: string, label?: string, big = false) =>
  L.divIcon({
    className: '',
    iconSize: [big ? 40 : 32, big ? 40 : 32],
    iconAnchor: [big ? 20 : 16, big ? 20 : 16],
    html: `<div style="display:flex;flex-direction:column;align-items:center;transform:translateY(0)">
      <div style="width:${big ? 36 : 28}px;height:${big ? 36 : 28}px;border-radius:50%;background:${color};border:3px solid #fff;
        box-shadow:0 1px 4px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;font-size:${big ? 18 : 14}px">${emoji}</div>
      ${label ? `<div style="margin-top:2px;padding:0 5px;border-radius:6px;background:#fff;color:#222;font:600 11px/16px sans-serif;
        white-space:nowrap;box-shadow:0 1px 2px rgba(0,0,0,.3)">${escapeHtml(label)}</div>` : ''}
    </div>`,
  });

/** Gösterilen noktalar değişince haritayı onlara sığdırır; pencere açılınca boyutu düzeltir. */
const FitBounds: React.FC<{ points: [number, number][]; fitKey: string }> = ({ points, fitKey }) => {
  const map = useMap();
  useEffect(() => {
    const id = window.setTimeout(() => {
      map.invalidateSize();
      if (points.length === 1) map.setView(points[0], 15);
      else if (points.length > 1) map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 16 });
    }, 150);
    return () => window.clearTimeout(id);
    // Yalnız odak değişince sığdırılır; yenilemede harita kaydırılmaz.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, fitKey]);
  return null;
};

const CourierMapDialog: React.FC<Props> = ({ open, onClose, branchId, courierId }) => {
  const { t } = useTranslation('salesSystem');
  const [data, setData] = useState<CourierMap | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [focusId, setFocusId] = useState<number | null>(courierId ?? null);
  const [nudging, setNudging] = useState(false);
  const [nudgeMsg, setNudgeMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (open) {
      setFocusId(courierId ?? null);
      setNudgeMsg(null);
    }
  }, [open, courierId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await courierModuleService.courierMap({ branchId }));
      setError(null);
      setNow(Date.now());
    } catch (e: any) {
      setError(e?.message || t('courierModule.map.loadError'));
    } finally {
      setLoading(false);
    }
  }, [branchId, t]);

  useEffect(() => {
    if (!open) return;
    void load();
    const timer = window.setInterval(() => void load(), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [open, load]);

  const couriers = data?.couriers ?? [];
  const focused = focusId != null ? couriers.find((c) => c.id === focusId) ?? null : null;
  const visible = focused ? [focused] : couriers;
  const branchOf = (c: CourierMapCourier) => data?.branches.find((b) => b.branchId === c.restaurantBranchId) ?? null;
  const shownBranches = focused
    ? [branchOf(focused)].filter(Boolean) as NonNullable<ReturnType<typeof branchOf>>[]
    : data?.branches ?? [];

  const points = useMemo(() => {
    const list: [number, number][] = [];
    visible.forEach((c) => {
      if (c.latitude != null && c.longitude != null) list.push([c.latitude, c.longitude]);
      if (focused) c.orders.forEach((o) => { if (o.latitude != null && o.longitude != null) list.push([o.latitude, o.longitude]); });
    });
    shownBranches.forEach((b) => { if (b.latitude != null && b.longitude != null) list.push([b.latitude, b.longitude]); });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, focusId]);

  const fitKey = `${focusId ?? 'all'}:${data ? 'loaded' : 'empty'}:${points.length > 0}`;

  const nudge = async (c: CourierMapCourier) => {
    setNudging(true);
    setNudgeMsg(null);
    try {
      await courierModuleService.nudgeLocation(c.id);
      setNudgeMsg({ ok: true, text: t('courierModule.map.nudgeSent', { name: c.name || '' }) });
      void load();
    } catch (e: any) {
      setNudgeMsg({ ok: false, text: e?.message || t('courierModule.map.nudgeError') });
    } finally {
      setNudging(false);
    }
  };

  const badge = (c: CourierMapCourier) => {
    const info = locationInfo(c, now);
    const label = info.freshness === 'off'
      ? t('courierModule.map.off')
      : info.freshness === 'none'
        ? t('courierModule.map.none')
        : agoText(t, info.minutes, c.locationAt);
    return <Chip size="small" color={freshnessColor(info.freshness)} label={`📍 ${label}`} sx={{ height: 22, fontSize: 11 }} />;
  };

  const detail = (c: CourierMapCourier) => {
    const info = locationInfo(c, now);
    const restaurant = branchOf(c);
    const needsNudge = info.freshness === 'off' || info.freshness === 'stale' || info.freshness === 'none';
    const offSince = parseUtc(c.locationStateAt);
    return (
      <Stack spacing={1}>
        {info.freshness === 'off' && (
          <Alert severity="warning" sx={{ py: 0 }}>
            {t(c.locationState === 'service_off' ? 'courierModule.map.offServiceAlert' : 'courierModule.map.offAlert', {
              since: offSince ? offSince.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : '—',
            })}
          </Alert>
        )}
        {info.freshness === 'stale' && <Alert severity="info" sx={{ py: 0 }}>{t('courierModule.map.staleAlert')}</Alert>}
        {info.freshness === 'none' && <Alert severity="info" sx={{ py: 0 }}>{t('courierModule.map.noneAlert')}</Alert>}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 2, rowGap: 0.5, fontSize: 13 }}>
          <Typography variant="body2" color="text.secondary">
            {info.freshness === 'off' ? t('courierModule.map.lastKnown') : t('courierModule.map.lastLocation')}
          </Typography>
          <Typography variant="body2" fontWeight={700}>
            {c.locationAt
              ? `${parseUtc(c.locationAt)?.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })} · ${agoText(t, info.minutes, c.locationAt)}`
              : '—'}
          </Typography>
          <Typography variant="body2" color="text.secondary">{t('courierModule.map.inHand')}</Typography>
          <Box>
            {c.orders.length === 0 ? (
              <Typography variant="body2" fontWeight={700}>{t('courierModule.map.noOrder')}</Typography>
            ) : (
              c.orders.map((o) => (
                <Typography key={o.activeOrderId} variant="body2" fontWeight={700}>
                  #{o.orderNumber}{o.deliveryAddress ? ` · ${o.deliveryAddress}` : ''}
                  {o.distanceKm != null && (
                    <Typography component="span" variant="caption" color="text.secondary"> · {t('courierModule.map.toDelivery', { km: kmText(o.distanceKm) })}</Typography>
                  )}
                </Typography>
              ))
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">{t('courierModule.map.toRestaurant')}</Typography>
          <Typography variant="body2" fontWeight={700}>
            {restaurant && restaurant.latitude == null
              ? <Typography component="span" variant="body2" color="warning.main">{t('courierModule.map.branchNoLocation', { name: restaurant.name })}</Typography>
              : c.restaurantDistanceKm != null
                ? `${kmText(c.restaurantDistanceKm)}${restaurant ? ` · ${restaurant.name}` : ''}`
                : '—'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {c.latitude != null && c.longitude != null && (
            <Button size="small" variant="outlined" startIcon={<OpenIcon />} href={googleMapsPointUrl(c.latitude, c.longitude)} target="_blank" rel="noopener noreferrer">
              {t('courierModule.map.openGoogle')}
            </Button>
          )}
          {needsNudge && (
            <Button size="small" variant="contained" color="warning" startIcon={nudging ? <CircularProgress size={14} color="inherit" /> : <NudgeIcon />}
              disabled={nudging} onClick={() => void nudge(c)}>
              {t('courierModule.map.nudge')}
            </Button>
          )}
        </Stack>
        {nudgeMsg && <Alert severity={nudgeMsg.ok ? 'success' : 'error'} sx={{ py: 0 }}>{nudgeMsg.text}</Alert>}
        <Typography variant="caption" color="text.secondary">{t('courierModule.map.straightLine')}</Typography>
      </Stack>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { height: { xs: '100%', md: '82vh' }, display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
        {focused ? <LocationIcon /> : <MapIcon />}
        <Typography variant="h6" fontWeight={800} sx={{ flex: 1, minWidth: 0 }} noWrap>
          {focused ? t('courierModule.map.titleOne', { name: focused.name || '' }) : t('courierModule.map.titleAll')}
        </Typography>
        {loading && <CircularProgress size={18} sx={{ color: 'inherit' }} />}
        {focused ? (
          <Chip label={t('courierModule.map.showAll')} onClick={() => setFocusId(null)}
            sx={{ bgcolor: 'common.white', color: 'primary.main', fontWeight: 700 }} />
        ) : null}
        <IconButton onClick={onClose} sx={{ color: 'inherit' }}><CloseIcon /></IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ borderRadius: 0 }}>{error}</Alert>}

      <Box sx={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 340px' }, gridTemplateRows: { xs: 'minmax(260px, 1fr) auto', md: '1fr' } }}>
        <Box sx={{ position: 'relative', minHeight: 260 }}>
          {open && (
            <MapContainer center={TURKEY_CENTER} zoom={6} maxZoom={MAP_MAX_ZOOM} style={{ position: 'absolute', inset: 0 }} scrollWheelZoom>
              <TileLayer url={MAP_TILE_URL} attribution={MAP_TILE_ATTRIBUTION} maxZoom={MAP_MAX_ZOOM} />
              <FitBounds points={points} fitKey={fitKey} />
              {shownBranches.filter((b) => b.latitude != null && b.longitude != null).map((b) => (
                <Marker key={`b-${b.branchId}`} position={[b.latitude!, b.longitude!]} icon={pin('🏪', '#1565c0', b.name)}>
                  <MapTooltip>{t('courierModule.map.restaurant', { name: b.name })}</MapTooltip>
                </Marker>
              ))}
              {focused && focused.orders.filter((o) => o.latitude != null && o.longitude != null).map((o) => (
                <Marker key={`o-${o.activeOrderId}`} position={[o.latitude!, o.longitude!]} icon={pin('🏠', '#6a1b9a', `#${o.orderNumber}`)}>
                  <Popup>
                    <b>#{o.orderNumber}</b>
                    {o.customerName ? <div>{o.customerName}</div> : null}
                    {o.deliveryAddress ? <div>{o.deliveryAddress}</div> : null}
                  </Popup>
                </Marker>
              ))}
              {visible.filter((c) => c.latitude != null && c.longitude != null).map((c) => {
                const info = locationInfo(c, now);
                const ago = info.freshness === 'off' ? t('courierModule.map.off') : agoText(t, info.minutes, c.locationAt);
                return (
                  <Marker key={`c-${c.id}`} position={[c.latitude!, c.longitude!]}
                    icon={pin('🛵', freshnessHex(info.freshness), `${c.name || ''} · ${ago}`, c.id === focusId)}
                    eventHandlers={{ click: () => setFocusId(c.id) }} />
                );
              })}
            </MapContainer>
          )}
          {data && points.length === 0 && (
            <Paper sx={{ position: 'absolute', left: 12, right: 12, top: 12, zIndex: 500, p: 1.5 }}>
              <Typography variant="body2">{t('courierModule.map.nothingToShow')}</Typography>
            </Paper>
          )}
        </Box>

        <Box sx={{ borderLeft: { md: 1 }, borderTop: { xs: 1, md: 0 }, borderColor: 'divider', overflowY: 'auto', p: 1.5 }}>
          {!data && loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
          ) : focused ? (
            detail(focused)
          ) : couriers.length === 0 ? (
            <Typography variant="body2" color="text.secondary">{t('courierModule.map.noCouriers')}</Typography>
          ) : (
            <Stack spacing={0.75}>
              <Typography variant="overline" color="text.secondary">{t('courierModule.map.listTitle', { count: couriers.length })}</Typography>
              {couriers.map((c) => (
                <Paper key={c.id} variant="outlined" onClick={() => setFocusId(c.id)}
                  sx={{ p: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1, '&:hover': { bgcolor: 'action.hover' } }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={700} fontSize={13} noWrap>{c.name}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      {c.state === 'on_road' ? t('courierModule.map.onRoad', { count: c.orders.length }) : t('courierModule.map.available')}
                      {c.restaurantDistanceKm != null ? ` · ${t('courierModule.map.toRestaurantShort', { km: kmText(c.restaurantDistanceKm) })}` : ''}
                    </Typography>
                  </Box>
                  {badge(c)}
                </Paper>
              ))}
            </Stack>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default CourierMapDialog;
